/**
 * Country reference data shared across the monorepo.
 *
 * `@local/utils` sits at the bottom of the workspace graph — its only `@local/*`
 * dependency is `@local/i18n`, which imports nothing local in turn, so importing
 * from here cannot create a cycle (`pnpm check:circular` covers it). Every other
 * package that needs this data (`payment`, `offer`, `tracking`, `identity`)
 * already depends on utils.
 *
 * This module stays pure data plus predicates: no market-slug normalization, so
 * it has no imports of its own. Callers resolve the base market first and pass a
 * plain ISO code in.
 */

/**
 * Destinations with no postal code system at all. The checkout and address-book
 * forms hide the field for these and write a placeholder, since the backend
 * still wants the key present.
 */
export const noPostCodeCountries = [
  { code: "AO", name: "Angola" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AW", name: "Aruba" },
  { code: "SH-AC", name: "Ascension island" }, // Non-standard code
  { code: "BS", name: "Bahamas" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BW", name: "Botswana" },
  { code: "BO", name: "Bolivia" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "CM", name: "Cameroon" },
  { code: "CF", name: "Central African Republic" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" },
  { code: "CD", name: "Congo the Democratic Republic of the" },
  { code: "CK", name: "Cook Islands" },
  { code: "CI", name: "Cote d'Ivoire" },
  { code: "CW", name: "Curaçao" },
  { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "FJ", name: "Fiji" },
  { code: "TF", name: "French Southern Territories" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GH", name: "Ghana" },
  { code: "GD", name: "Grenada" },
  { code: "GY", name: "Guyana" },
  { code: "HM", name: "Heard and McDonald Islands" },
  { code: "HK", name: "Hong Kong" },
  { code: "KI", name: "Kiribati" },
  { code: "LY", name: "Libya" },
  { code: "MO", name: "Macau" },
  { code: "MW", name: "Malawi" },
  { code: "ML", name: "Mali" },
  { code: "MR", name: "Mauritania" },
  { code: "NR", name: "Nauru" },
  { code: "AN", name: "Netherlands Antilles" }, // Obsolete code
  { code: "NU", name: "Niue" },
  { code: "KP", name: "North Korea" },
  { code: "QA", name: "Qatar" },
  { code: "RW", name: "Rwanda" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "ST", name: "Sao Tome and Principe" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SR", name: "Suriname" },
  { code: "SY", name: "Syria" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" },
  { code: "TK", name: "Tokelau" },
  { code: "TO", name: "Tonga" },
  { code: "TV", name: "Tuvalu" },
  { code: "UG", name: "Uganda" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "VU", name: "Vanuatu" },
  { code: "YE", name: "Yemen" },
  { code: "ZW", name: "Zimbabwe" },
]

/**
 * The political EU-27.
 *
 * This is the *legal* EU, not the set of countries the `eu` storefront ships to —
 * see `EU_MARKET_COUNTRIES` for that. Keep the two apart: this list drives GDPR
 * and consent bucketing (`@local/tracking`), the EUR↔country mapping in
 * `I18n.getCountryCodesFromCurrency`, and `I18n.isCountryInsideEU`. Adding a
 * non-member here to make it shippable would silently change all three.
 */
export const EU_COUNTRIES: readonly string[] = [
  "AT", // Austria
  "BE", // Belgium
  "BG", // Bulgaria
  "HR", // Croatia
  "CY", // Cyprus
  "CZ", // Czech Republic
  "DK", // Denmark
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "GR", // Greece
  "HU", // Hungary
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "NL", // Netherlands
  "PL", // Poland
  "PT", // Portugal
  "RO", // Romania
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain
  "SE", // Sweden
]

/**
 * Countries the `eu` storefront sells and ships to: the EU-27 plus the EEA
 * members and European microstates we serve from the same fulfilment centre and
 * bill in EUR.
 *
 * `CH` and `GB` are deliberately absent — they have their own markets and
 * currencies. `LI` is here despite using CHF and sitting inside the Swiss
 * customs union: it is served from `eu` and billed in EUR by product decision.
 */
export const EU_MARKET_COUNTRIES: readonly string[] = [
  ...EU_COUNTRIES,
  "NO", // Norway (EEA)
  "IS", // Iceland (EEA)
  "LI", // Liechtenstein (EEA)
  "AD", // Andorra (EUR by monetary agreement)
  "MC", // Monaco (EUR by monetary agreement)
  "SM", // San Marino (EUR by monetary agreement)
]

/**
 * Is `code` a destination the `eu` market ships to? Case-insensitive, so it
 * accepts both the upper-case ISO codes the address forms use and the
 * lower-case ones geo headers hand back.
 */
export const isEuMarketCountry = (code: string): boolean =>
  EU_MARKET_COUNTRIES.includes(code.toUpperCase())

/** The floor applied to every country without a `POSTAL_MIN_LENGTH` entry. */
export const DEFAULT_POSTAL_MIN_LENGTH = 4

/** The ceiling applied to every country's postal code. */
export const POSTAL_MAX_LENGTH = 12

/**
 * Countries whose postal codes are shorter than the four characters the address
 * schemas otherwise demand. Only the exceptions live here; everything else
 * falls through to `DEFAULT_POSTAL_MIN_LENGTH`.
 */
const POSTAL_MIN_LENGTH: Record<string, number> = {
  IS: 3, // Iceland: three digits, e.g. 101 (Reykjavík), 600 (Akureyri)
}

/** Minimum postal-code length to accept for `country`. */
export const postalMinLengthFor = (country: string): number =>
  POSTAL_MIN_LENGTH[country.toUpperCase()] ?? DEFAULT_POSTAL_MIN_LENGTH
